import { db } from './codexDB';
import { SEED_TS } from '@/lib/constants';

/**
 * 캐논 핵심 초상 시드 — 캐릭터 이미지 시트를 `assets`(kind='portrait') Blob으로 적재.
 * public/portraits/{id}-{i}.png 를 fetch → Blob → 결정적 id로 put(멱등). 인물 1명당 여러 컷(앞/옆/뒤/흉상), i=0=대표.
 * CharacterDossier가 useEntityAssetUrl(s)로 표시. 엔티티 재시드에도 assets는 보존된다.
 *
 * 교체(버전): 같은 파일명을 새 내용으로 바꾸면 멱등 skip이 옛 Blob을 남기므로, spec.v를 두어 asset id에 버전을 박는다.
 * 매 부팅 재조정 — 이 인물의 시드 관리 에셋 중 현재 기대집합에 없는 것(옛 버전·삭제 컷)을 지운다. 사용자 업로드(다른 id)는 보존.
 */

interface PortraitSpec {
  id: string; // 엔티티 id
  count: number; // 컷 수
  v?: number; // 내용 교체 시 bump → asset id에 버전 박아 강제 재시드
}

// 파일명: public/portraits/{id}-{i}.png. i=0 = 대표(정면). (스콘스.png = 빅터 스콘 = victor)
const PORTRAITS: PortraitSpec[] = [
  { id: 'radia', count: 4 }, // 정면·흉상·측면·후면
  { id: 'rasia', count: 5 }, // 정면·흉상·측면·후면·후면2
  { id: 'victor', count: 5, v: 2 }, // 스콘스 정단 보스(빅터 스콘) — 5컷 데이터시트로 교체(정면·후면·측면·흉상·전략)
  { id: 'alma', count: 5, v: 2 }, // 아이샤 알마 — 5컷 데이터시트로 교체(정면·후면·측면·흉상·전신측면)
  { id: 'ian', count: 5, v: 2 }, // 이안 로크 — 5컷 데이터시트로 교체(정면·후면·측면·흉상·라이프스타일)
  { id: 'jerome', count: 1 }, // 제롬
  { id: 'kylis', count: 5, v: 2 }, // 5컷 데이터시트로 교체(정면·후면·측면·흉상·전략)
];

const assetIdFor = (s: PortraitSpec, i: number): string =>
  s.v ? `seed-portrait-${s.id}-v${s.v}-${i}` : `seed-portrait-${s.id}-${i}`;

/** 부팅 시(멱등) — 인물별로 stale 시드 에셋 정리 후 누락 컷만 fetch해 적재. 네트워크/테스트 실패는 조용히 skip. */
export async function seedPortraits(): Promise<void> {
  if (typeof fetch === 'undefined') return; // jsdom 등 — skip
  for (const spec of PORTRAITS) {
    const expected = new Set(Array.from({ length: spec.count }, (_, i) => assetIdFor(spec, i)));
    try {
      // 재조정 — 시드 관리 에셋(seed-portrait-{id}-*) 중 기대집합에 없는 것 제거(옛 버전·줄어든 컷). 사용자 업로드는 건드리지 않음.
      const managed = await db.assets
        .where('entityId')
        .equals(spec.id)
        .filter((a) => a.kind === 'portrait' && a.id.startsWith(`seed-portrait-${spec.id}-`))
        .toArray();
      const stale = managed.filter((a) => !expected.has(a.id)).map((a) => a.id);
      if (stale.length) await db.assets.bulkDelete(stale);

      // 누락 컷 적재
      for (let i = 0; i < spec.count; i++) {
        const assetId = assetIdFor(spec, i);
        if (await db.assets.get(assetId)) continue; // 이미 적재됨
        const res = await fetch(`/portraits/${spec.id}-${i}.png`);
        if (!res.ok) continue;
        const blob = await res.blob();
        if (!blob.size) continue;
        await db.assets.put({
          id: assetId,
          entityId: spec.id,
          kind: 'portrait',
          blob,
          mime: blob.type || 'image/png',
          name: `${spec.id}-${i}`,
          createdAt: SEED_TS + i, // 대표(i=0)가 가장 앞 — 썸네일·갤러리 순서 고정
        });
      }
    } catch {
      /* 오프라인/테스트 — 다음 부팅에 재시도 */
    }
  }
}
